import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import { Field, Formik } from "formik";
import { Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { newFolderSchema } from "./validation/newFolder";
import { useUserQuery } from "../../graphql/general.hooks";
import {
  useFolderNewMutation,
  useFolderShowLazyQuery,
  useFoldersUserQuery,
} from "../../graphql/folder.hooks";
import { UserDocument } from "../../graphql/menu.hooks";
import { useIsNoShared } from "../../hooks/useIsNoShared";
import { StyledInput } from "../../components/inputs/styledInput";
import ReactSelect from "../../components/inputs/select";
import { SelectOption } from "../../types";
import { useUserAccessQuery } from "../../graphql/access.hooks";
import { useGetOptions } from "../../hooks/useGetOptions";

export const NewFolder = () => {
  const lng = useGetUrlLocale();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { domainOptions } = useGetOptions({});

  const searchParams = new URLSearchParams(location.search);
  const parentDomain = +searchParams.get("parentDomain");
  const parentFolder = +searchParams.get("parentFolder");

  const { data, loading: userLoading } = useUserQuery();
  const [newFolder, { loading: createLoading }] = useFolderNewMutation({
    refetchQueries: [
      {
        query: UserDocument,
      },
    ],
  });
  const { refetch: allFoldersRefetch } = useFoldersUserQuery();
  const { refetch: refetchUserAccess } = useUserAccessQuery();

  const [fetchFolderData, { data: folderData }] = useFolderShowLazyQuery();

  const domainTitle = parentDomain
    ? data?.user.domains.find(({ id }) => id === parentDomain)?.title
    : folderData?.folderShow.folder.domain.title;

  const folderTitle = parentFolder
    ? folderData?.folderShow.folder.title
    : undefined;

  useEffect(() => {
    if (parentFolder) {
      fetchFolderData({ variables: { folderId: parentFolder } });
    }
  }, [parentFolder, fetchFolderData]);

  useEffect(() => {
    document.title = "New Folder";
  }, []);

  interface INewFolder {
    title: string;
    url: string;
    parentDomain: number | null;
    parentFolder: number | null;
  }

  const initialValues: INewFolder = {
    title: "",
    url: "",
    parentDomain: parentDomain || null,
    parentFolder: null,
  };

  const tryCreateNewFolder = async (values: INewFolder) => {
    try {
      if (parentDomain !== null && !parentFolder) {
        const { data } = await newFolder({
          variables: {
            ...values,
            parentDomain: +values.parentDomain,
          },
        });
        if (data.folderNew.id && +values.parentDomain) {
          allFoldersRefetch();
          navigate(`${lng}/projects/${+values.parentDomain}`);
        }
      } else if (parentFolder !== null) {
        const { data } = await newFolder({
          variables: {
            ...values,
            parentDomain: null,
            parentFolder: +parentFolder,
          },
        });
        if (data.folderNew.id && parentFolder) {
          refetchUserAccess();
          allFoldersRefetch();
          navigate(
            `${lng}/projects/${folderData?.folderShow.folder.domain.id}/folder/${folderData.folderShow.folder.id}`
          );
        }
      }
    } catch (err) {
      console.error(err);
    }
  };
  const isNoShared = useIsNoShared();
  const goBack = () => {
    navigate(-1);
  };

  return (
    <div className="px-lg-3">
      <h1 className="fs-1 fw-bold">{t("folder.create")}</h1>
      <div className="text-gray mb-4 mb-md-5">
        <Link to={`${lng}/projects`}>{t("folder.projects")}</Link>
        {!!parentDomain && (
          <>
            <span className="px-2">/</span>
            <Link to={`${lng}/projects/${parentDomain}`}>{domainTitle}</Link>
          </>
        )}
        {folderTitle && (
          <>
            <span className="px-2">/</span>
            <Link
              to={`${lng}/projects/${folderData.folderShow.folder.domain.id}/folder/${folderData.folderShow.folder.id}`}
            >
              {folderTitle}
            </Link>
          </>
        )}
        <span className="px-2">/</span>
        {t("folder.create")}
      </div>
      <div className="text-dark bg-white py-4 py-lg-5 d-flex justify-content-center rounded">
        <Formik
          initialValues={initialValues}
          validationSchema={newFolderSchema(t)}
          onSubmit={tryCreateNewFolder}
        >
          {({ handleSubmit, touched, errors, setFieldValue, values }) => (
            <Form className="form-container px-4 p-lg-0">
              <div className="form-floating mb-3">
                <StyledInput
                  name="title"
                  label={t("folder.folderName")}
                  error={!!(touched.title && errors.title)}
                />
              </div>
              <div className={`form-floating ${isNoShared ? "mb-3" : "mb-5"}`}>
                <StyledInput
                  name="url"
                  label={t("folder.urlName")}
                  error={!!(touched.url && errors.url)}
                />
              </div>
              {isNoShared && (
                <Field name="parentDomain">
                  {() => (
                    <ReactSelect
                      className="mb-3"
                      options={domainOptions}
                      value={domainOptions.find(
                        (domain) => domain.value === +values.parentDomain
                      )}
                      onChange={(option: SelectOption) => {
                        setFieldValue("parentDomain", option.value);
                      }}
                      placeholder="Select Project"
                      isLoading={userLoading}
                      label={t("menu.project")}
                    />
                  )}
                </Field>
              )}
              <div className="d-flex align-items-center justify-content-between">
                <button
                  type="button"
                  className="btn btn-outline-dark dark-hover fw-normal buttomCol col-md-4"
                  onClick={goBack}
                >
                  {t("folder.cancel")}
                </button>
                <button
                  type="button"
                  disabled={createLoading}
                  onClick={() => handleSubmit()}
                  className="btn btn-primary text-white buttomCol col-md-4"
                >
                  {t("projects.Save")}
                  {createLoading && (
                    <Spinner animation="border" role="status" className="ms-2">
                      <span className="visually-hidden">
                        {t("profile.loading")}...
                      </span>
                    </Spinner>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
